/**
 * @fileoverview Performance Debugging Utility
 * Easy-to-use performance monitoring and debugging for cart system
 */

import { cartPerformance } from './Performance.js';
import { Debug } from './index.js';

/**
 * Performance debugger for cart operations
 */
export class PerformanceDebugger {
  constructor() {
    this.isEnabled = false;
    this.alertThresholds = {
      'add-to-cart': 1000, // 1 second
      'quantity-change': 500, // 0.5 seconds
      'cart-load': 2000, // 2 seconds
      'smart-update': 100 // 0.1 seconds
    };
    this.setupConsoleCommands();
  }

  /**
   * Enable performance debugging
   */
  enable() {
    this.isEnabled = true;
    Debug.log('info', '🚀 Cart Performance Debugging Enabled');
    this.startPerformanceMonitoring();
  }

  /**
   * Disable performance debugging
   */
  disable() {
    this.isEnabled = false;
    Debug.log('info', '⏸️ Cart Performance Debugging Disabled');
  }

  /**
   * Toggle performance debugging
   */
  toggle() {
    this.isEnabled ? this.disable() : this.enable();
  }

  /**
   * Start continuous performance monitoring
   */
  startPerformanceMonitoring() {
    if (!this.isEnabled) return;

    // Monitor performance every 10 seconds
    setInterval(() => {
      if (this.isEnabled) {
        this.checkPerformanceThresholds();
      }
    }, 10000);
  }

  /**
   * Check if any operations exceed performance thresholds
   */
  checkPerformanceThresholds() {
    const metrics = cartPerformance.getMetrics();
    
    for (const [operation, metric] of Object.entries(metrics)) {
      const threshold = this.getThresholdForOperation(operation);
      
      if (metric.averageTime > threshold) {
        console.warn(`⚠️ Cart Performance Alert: ${operation} averaging ${metric.averageTime}ms (threshold: ${threshold}ms)`);
      }
      
      if (metric.maxTime > threshold * 2) {
        console.error(`🚨 Cart Performance Critical: ${operation} max time ${metric.maxTime}ms (threshold: ${threshold}ms)`);
      }
    }
  }

  /**
   * Get threshold for a specific operation
   * @param {string} operation - Operation name
   * @returns {number} Threshold in milliseconds
   */
  getThresholdForOperation(operation) {
    for (const [key, threshold] of Object.entries(this.alertThresholds)) {
      if (operation.includes(key)) {
        return threshold;
      }
    }
    return 1000; // Default threshold
  }

  /**
   * Display performance summary
   */
  summary() {
    console.group('🚀 Cart Performance Summary');
    cartPerformance.logMetrics();
    console.groupEnd();
  }

  /**
   * Display detailed performance analysis
   */
  analyze() {
    const metrics = cartPerformance.getMetrics();
    
    console.group('📊 Cart Performance Analysis');
    
    // Overall stats
    const totalOperations = Object.values(metrics).reduce((sum, metric) => sum + metric.count, 0);
    const averageTime = Object.values(metrics).reduce((sum, metric) => sum + metric.averageTime, 0) / Object.keys(metrics).length;
    
    console.log(`Total Operations: ${totalOperations}`);
    console.log(`Average Response Time: ${averageTime.toFixed(2)}ms`);
    
    // Slowest operations
    const slowestOperations = Object.entries(metrics)
      .sort(([,a], [,b]) => b.averageTime - a.averageTime)
      .slice(0, 5);
    
    console.group('🐌 Slowest Operations');
    slowestOperations.forEach(([name, metric]) => {
      console.log(`${name}: ${metric.averageTime}ms (${metric.count} calls)`);
    });
    console.groupEnd();
    
    // Most frequent operations
    const mostFrequent = Object.entries(metrics)
      .sort(([,a], [,b]) => b.count - a.count)
      .slice(0, 5);
    
    console.group('🔄 Most Frequent Operations');
    mostFrequent.forEach(([name, metric]) => {
      console.log(`${name}: ${metric.count} calls (${metric.averageTime}ms avg)`);
    });
    console.groupEnd();
    
    // Performance recommendations
    this.generateRecommendations(metrics);
    
    console.groupEnd();
  }

  /**
   * Generate performance recommendations
   * @param {Object} metrics - Performance metrics
   */
  generateRecommendations(metrics) {
    console.group('💡 Performance Recommendations');
    
    Object.entries(metrics).forEach(([name, metric]) => {
      if (metric.averageTime > this.getThresholdForOperation(name)) {
        console.log(`⚠️ ${name}: Consider optimizing (${metric.averageTime}ms avg)`);
      }
      
      if (metric.count > 100) {
        console.log(`🔄 ${name}: High frequency operation (${metric.count} calls) - ensure efficient implementation`);
      }
      
      if (metric.maxTime > metric.averageTime * 3) {
        console.log(`📈 ${name}: High variability (max: ${metric.maxTime}ms, avg: ${metric.averageTime}ms) - investigate edge cases`);
      }
    });
    
    console.groupEnd();
  }

  /**
   * Export performance data
   * @returns {Object} Performance data
   */
  export() {
    const data = {
      timestamp: new Date().toISOString(),
      metrics: cartPerformance.getMetrics(),
      userAgent: navigator.userAgent,
      url: window.location.href
    };
    
    console.log('📊 Performance Data Exported:', data);
    return data;
  }

  /**
   * Clear all performance data
   */
  clear() {
    cartPerformance.clearMetrics();
    console.log('🗑️ Performance data cleared');
  }

  /**
   * Set up console commands for easy debugging
   */
  setupConsoleCommands() {
    if (typeof window === 'undefined') return;
    
    // Add global debugging commands
    window.cartDebug = {
      performance: {
        enable: () => this.enable(),
        disable: () => this.disable(),
        toggle: () => this.toggle(),
        summary: () => this.summary(),
        analyze: () => this.analyze(),
        export: () => this.export(),
        clear: () => this.clear()
      }
    };
    
    // Add helpful info
    console.log('🛠️ Cart Debug Commands Available:');
    console.log('  cartDebug.performance.enable()  - Enable performance monitoring');
    console.log('  cartDebug.performance.summary() - Show performance summary');
    console.log('  cartDebug.performance.analyze() - Detailed performance analysis');
    console.log('  cartDebug.performance.export()  - Export performance data');
  }

  /**
   * Monitor specific operation
   * @param {string} operationName - Name of the operation
   * @param {Function} operation - Function to monitor
   * @returns {Promise<any>} Operation result
   */
  async monitor(operationName, operation) {
    const marker = cartPerformance.createStartingMarker(operationName);
    
    try {
      const result = await operation();
      
      if (marker) {
        cartPerformance.measureFromMarker(marker);
      }
      
      return result;
    } catch (error) {
      console.error(`❌ Operation failed: ${operationName}`, error);
      throw error;
    }
  }
}

// Export singleton instance
export const performanceDebugger = new PerformanceDebugger();

// Auto-enable in development
if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
  performanceDebugger.enable();
} 